import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChromeConfigProvider, type ChromeConfig, type ChromeLinkProps } from '@gjl/studio-chrome'
import { Button, useFeedback } from '@/components/ui'
import { NEWSLETTER_ENDPOINT } from '@/lib/newsletter'

/**
 * Prefixes owned by a DIFFERENT Vite build on this same origin. fuguelab.com is
 * served by three: studio/client owns `/`, studio/hymnal owns `/hymnal/*`, and
 * projects/tooling/design-lab owns `/design/*`.
 *
 * A react-router `<Link to="/design/">` rendered here would be handled by
 * STUDIO's router, match none of its routes, and land the visitor on a
 * client-side 404 — without ever hitting the network, even though the page is
 * really there. Crossing a bundle boundary requires a real navigation.
 *
 * Add a prefix here whenever a nav entry points outside studio's own routes.
 */
const CROSS_BUNDLE_PREFIXES = ['/hymnal/', '/design/']

/**
 * How studio wires itself into the shared chrome.
 *
 * Studio owns `/`, so nav targets are normally its own routes and go through
 * react-router for a client-side transition. The exception is the list above.
 * (The hymnal's adapter renders plain `<a>` for everything — see the note in
 * the package's `config.tsx` for why that is not a downgrade but the only
 * correct choice on the other side of the bundler boundary.)
 */
function StudioLink({ href, children, ...rest }: ChromeLinkProps) {
  if (CROSS_BUNDLE_PREFIXES.some((p) => href.startsWith(p))) {
    return (
      <a href={href} {...rest}>
        {children}
      </a>
    )
  }
  return (
    <Link to={href} {...rest}>
      {children}
    </Link>
  )
}

/**
 * Must render inside `<FeedbackProvider>` — the newsletter form reports through
 * studio's toast host, and `useFeedback()` throws outside it.
 */
export function StudioChromeProvider({ children }: { children: ReactNode }) {
  const { toast } = useFeedback()

  const config: ChromeConfig = {
    Link: StudioLink,
    newsletter: {
      endpoint: NEWSLETTER_ENDPOINT,
      Button,
      notify: toast,
    },
  }

  return <ChromeConfigProvider config={config}>{children}</ChromeConfigProvider>
}
